import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";

export interface DiscordChannel {
  id: string;
  name: string;
  /** Discord channel type: 0=text, 2=voice, 4=category, 5=announcement, 13=stage, 15=forum */
  type: number;
}

export interface DiscordGuild {
  id: string;
  name: string;
  icon?: string | null;
}

/** Discord channel type constants */
export const DISCORD_CHANNEL_TYPE_TEXT = 0;
export const DISCORD_CHANNEL_TYPE_ANNOUNCEMENT = 5;

function isDiscordChannel(value: unknown): value is DiscordChannel {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as DiscordChannel).id === "string" &&
    typeof (value as DiscordChannel).name === "string" &&
    typeof (value as DiscordChannel).type === "number"
  );
}

function isDiscordGuild(value: unknown): value is DiscordGuild {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as DiscordGuild).id === "string" &&
    typeof (value as DiscordGuild).name === "string"
  );
}

/** Parse the Pipedream response into a flat array from various nesting shapes. */
function extractArray(result: Record<string, unknown> | undefined): unknown[] {
  if (!result) return [];
  if (Array.isArray(result.data)) return result.data as unknown[];
  if (Array.isArray(result.exports)) return result.exports as unknown[];
  if (result.data && typeof result.data === "object") {
    const nested = result.data as Record<string, unknown>;
    if (Array.isArray(nested.ret)) return nested.ret;
    if (Array.isArray(nested.channels)) return nested.channels;
    if (Array.isArray(nested.guilds)) return nested.guilds;
  }
  return [];
}

/**
 * Fetch the Discord servers (guilds) the bot has been added to.
 * Calls the list-channels action with no guildId.
 */
export async function fetchDiscordGuilds(connectorId: string, connectorType: string): Promise<DiscordGuild[]> {
  const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/actions/discord-bot-list-channels/test`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      inputs: {},
      connector: { id: connectorId, type: connectorType },
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch Discord servers (HTTP ${response.status})`);
  }

  const data = await response.json();
  const result = (data as { data?: { result?: Record<string, unknown> } })?.data?.result;
  const raw = extractArray(result);

  return raw.filter(isDiscordGuild).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Fetch Discord channels for a given server via the
 * discord-bot-list-channels action test endpoint.
 *
 * Filters to text channels (type 0) and announcement channels (type 5)
 * since those are the channels a bot can send messages to.
 */
export async function fetchDiscordChannels(
  connectorId: string,
  connectorType: string,
  guildId: string,
): Promise<DiscordChannel[]> {
  const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/actions/discord-bot-list-channels/test`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      inputs: { guildId },
      connector: { id: connectorId, type: connectorType },
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch Discord channels (HTTP ${response.status})`);
  }

  const data = await response.json();
  const result = (data as { data?: { result?: Record<string, unknown> } })?.data?.result;
  const raw = extractArray(result);

  if (raw.length === 0 && result) {
    console.warn("[discord-channels] Unexpected response shape:", Object.keys(result));
  }

  return raw
    .filter(
      (ch): ch is DiscordChannel =>
        isDiscordChannel(ch) &&
        (ch.type === DISCORD_CHANNEL_TYPE_TEXT || ch.type === DISCORD_CHANNEL_TYPE_ANNOUNCEMENT),
    )
    .sort((a, b) => a.name.localeCompare(b.name));
}
